import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Undo2, Calculator, History } from "lucide-react";
import { toast } from "sonner";

const NumInput = ({ testid, value, onChange, step = "0.01", suffix, ...rest }) => (
  <div className="relative">
    <Input data-testid={testid} type="number" step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className={suffix ? "pr-10" : ""} {...rest}/>
    {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">{suffix}</span>}
  </div>
);

const PricingConfig = () => {
  const [cfg, setCfg] = useState(null);
  const [orig, setOrig] = useState(null);
  const [history, setHistory] = useState([]);
  const [preview, setPreview] = useState({ travel_mode: "car", route_km: 60, detour_km: 5, size: "medium" });
  const [previewResult, setPreviewResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [{ data }, hist] = await Promise.all([
      adminApi.get("/config"),
      adminApi.get("/config/history"),
    ]);
    setCfg(structuredClone(data.config));
    setOrig(structuredClone(data.config));
    setHistory(hist.data);
    runPreview({ travel_mode: "car", route_km: 60, detour_km: 5, size: "medium" });
  };
  useEffect(() => { load(); }, []);

  const runPreview = async (p) => {
    try {
      const r = await adminApi.post("/config/preview", p);
      setPreviewResult(r.data);
    } catch (e) {
      setPreviewResult(null);
    }
  };

  if (!cfg) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  const set = (path, val) => {
    const c = structuredClone(cfg);
    const keys = path.split(".");
    let node = c;
    for (let i = 0; i < keys.length - 1; i++) node = node[keys[i]];
    node[keys[keys.length - 1]] = val;
    setCfg(c);
  };

  const diff = () => {
    const rows = [];
    const scan = (o, n, prefix = "") => {
      const keys = new Set([...Object.keys(o || {}), ...Object.keys(n || {})]);
      for (const k of keys) {
        const ov = (o || {})[k], nv = (n || {})[k];
        if (typeof nv === "object" && nv !== null && !Array.isArray(nv)) {
          scan(ov || {}, nv, prefix + k + ".");
        } else if (JSON.stringify(ov) !== JSON.stringify(nv)) {
          rows.push({ field: prefix + k, before: ov, after: nv });
        }
      }
    };
    scan(orig, cfg);
    return rows;
  };
  const changes = diff();
  const dirty = changes.length > 0;

  const save = async () => {
    setSaving(true);
    try {
      // Only send changed top-level fields
      const changedTop = new Set(changes.map(c => c.field.split(".")[0]));
      const patch = {};
      for (const k of changedTop) patch[k] = cfg[k];
      await adminApi.put("/config", patch);
      toast.success("Pricing saved");
      setConfirmOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-8">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Pricing configuration</h1>
          <p className="text-sm text-slate-500 mt-1">Editing these values affects only NEW parcels. Existing parcels keep their locked-in price.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setCfg(structuredClone(orig))} disabled={!dirty} data-testid="btn-revert"><Undo2 className="w-4 h-4 mr-1"/> Revert</Button>
          <Button onClick={() => setConfirmOpen(true)} disabled={!dirty || saving} data-testid="btn-save" className="bg-slate-900 hover:bg-slate-800">
            <Save className="w-4 h-4 mr-1"/> Save ({changes.length})
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 space-y-4">
          <Card title="Core fees">
            <Grid>
              <Field label="Handover fee (₹)"><NumInput testid="cfg-handover" value={cfg.handover_fee} onChange={v => set("handover_fee", v)}/></Field>
              <Field label="Commission (0–0.50)"><NumInput testid="cfg-commission" step="0.01" value={cfg.commission_rate} onChange={v => set("commission_rate", v)}/></Field>
              <Field label="Max detour to match (km)"><NumInput testid="cfg-max-detour" step="1" value={cfg.max_detour_km} onChange={v => set("max_detour_km", v)} suffix="km"/></Field>
              <Field label="Parcel alert radius (km)"><NumInput testid="cfg-alert-radius" step="1" value={cfg.parcel_alert_radius_km} onChange={v => set("parcel_alert_radius_km", v)} suffix="km"/></Field>
              <Field label="Minimum withdrawal (₹)"><NumInput testid="cfg-min-withdrawal" step="50" value={cfg.min_withdrawal_rupees} onChange={v => set("min_withdrawal_rupees", v)} suffix="₹"/></Field>
            </Grid>
            <div className="text-[11px] text-slate-500 mt-2">
              A trip is shown to a sender only if its extra distance to pick up + drop the parcel is at or below this value.
              Lower = tighter matches (fewer carriers, less detour); higher = more choices but longer routes.
            </div>
          </Card>

          <Card title="Minimum carrier earnings floor — by trip distance">
            <div className="text-xs text-slate-500 mb-2">Short trips get a lower floor; the first tier that the distance fits into wins. Editable rows below.</div>
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="text-left w-24">Up to (km)</th><th className="text-left">Floor (₹)</th></tr></thead>
              <tbody>
                {(cfg.min_earnings_tiers || []).map((t, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-1.5"><NumInput testid={`cfg-floor-max-${i}`} step="1" value={t.max_km} onChange={v => set(`min_earnings_tiers.${i}.max_km`, v)}/></td>
                    <td className="py-1.5"><NumInput testid={`cfg-floor-value-${i}`} step="1" value={t.floor} onChange={v => set(`min_earnings_tiers.${i}.floor`, v)}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <Card title="Route per-km rates (₹/km)">
            <Grid>
              <Field label="Bike / scooter"><NumInput testid="cfg-route-km-bike" value={cfg.route_per_km_rate?.bike} onChange={v => set("route_per_km_rate.bike", v)} suffix="₹/km"/></Field>
              <Field label="Car"><NumInput testid="cfg-route-km-car" value={cfg.route_per_km_rate?.car} onChange={v => set("route_per_km_rate.car", v)} suffix="₹/km"/></Field>
              <Field label="SUV / tempo"><NumInput testid="cfg-route-km-suv" value={cfg.route_per_km_rate?.suv} onChange={v => set("route_per_km_rate.suv", v)} suffix="₹/km"/></Field>
            </Grid>
            <div className="text-[11px] text-slate-500 mt-2">Charged on the parcel's direct pickup → drop distance.</div>
          </Card>

          <Card title="Detour per-km rates (₹/km)">
            <Grid>
              <Field label="Bike / scooter"><NumInput testid="cfg-km-bike" value={cfg.per_km_rate?.bike} onChange={v => set("per_km_rate.bike", v)} suffix="₹/km"/></Field>
              <Field label="Car"><NumInput testid="cfg-km-car" value={cfg.per_km_rate?.car} onChange={v => set("per_km_rate.car", v)} suffix="₹/km"/></Field>
              <Field label="SUV / tempo"><NumInput testid="cfg-km-suv" value={cfg.per_km_rate?.suv} onChange={v => set("per_km_rate.suv", v)} suffix="₹/km"/></Field>
            </Grid>
            <div className="text-[11px] text-slate-500 mt-2">Charged on the carrier's extra km to pick up and drop the parcel.</div>
          </Card>

          <Card title="Pre-booked travel handling fees (bus / train)">
            <div className="text-xs text-slate-500 mb-2">Flat fee tier by parcel-route distance. Applied instead of a per-km rate. Flight is disabled everywhere in the app but the config supports it if you re-enable later.</div>
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="text-left w-24">Up to (km)</th><th className="text-left">Fee (₹)</th></tr></thead>
              <tbody>
                {(cfg.transit_handling_tiers || []).map((t, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-1.5"><NumInput testid={`cfg-tier-max-${i}`} step="1" value={t.max_km} onChange={v => set(`transit_handling_tiers.${i}.max_km`, v)}/></td>
                    <td className="py-1.5"><NumInput testid={`cfg-tier-fee-${i}`} step="1" value={t.fee} onChange={v => set(`transit_handling_tiers.${i}.fee`, v)}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <Card title="Size uplift — percentage of the distance component">
            <div className="text-xs text-slate-500 mb-2">The distance component of the price is uplifted by this fraction (0.10 = +10%). Envelope stays at 0, XL doubles it.</div>
            <Grid>
              {["envelope","small","medium","large","xl"].map(k => (
                <Field key={k} label={`${k.toUpperCase()} uplift`}><NumInput testid={`cfg-size-${k}`} step="0.01" value={cfg.size_multiplier?.[k]} onChange={v => set(`size_multiplier.${k}`, v)} suffix="fraction"/></Field>
              ))}
            </Grid>
          </Card>

          <Card title="Door add-ons (transit only) — ₹, 100 % goes to carrier">
            <Grid>
              <Field label="Door pickup fee"><NumInput testid="cfg-door-pickup" value={cfg.door_pickup_fee} onChange={v => set("door_pickup_fee", v)}/></Field>
              <Field label="Door delivery fee"><NumInput testid="cfg-door-delivery" value={cfg.door_delivery_fee} onChange={v => set("door_delivery_fee", v)}/></Field>
            </Grid>
          </Card>

          <Card title="CarryIt Protection">
            <Grid>
              <Field label="Fee % of declared value (0–0.20)"><NumInput testid="cfg-protection-pct" step="0.001" value={cfg.protection_fee_pct} onChange={v => set("protection_fee_pct", v)} suffix="fraction"/></Field>
              <Field label="Minimum fee (₹)"><NumInput testid="cfg-protection-min" value={cfg.protection_min} onChange={v => set("protection_min", v)}/></Field>
            </Grid>
          </Card>

          <Card title="Trust tier declared-value caps (₹)">
            <Grid>
              <Field label="NEW cap"><NumInput testid="cfg-tier-cap-new" value={cfg.tier_value_caps?.new} onChange={v => set("tier_value_caps.new", v)}/></Field>
              <Field label="VERIFIED cap"><NumInput testid="cfg-tier-cap-verified" value={cfg.tier_value_caps?.verified} onChange={v => set("tier_value_caps.verified", v)}/></Field>
              <Field label="TRUSTED cap"><NumInput testid="cfg-tier-cap-trusted" value={cfg.tier_value_caps?.trusted} onChange={v => set("tier_value_caps.trusted", v)}/></Field>
            </Grid>
          </Card>
        </div>

        <div className="space-y-4">
          <Card title={<><Calculator className="w-4 h-4 inline mr-1"/> Live preview</>}>
            <div className="space-y-2">
              <label className="text-[11px] uppercase text-slate-500 tracking-wider">Travel mode</label>
              <Select value={preview.travel_mode} onValueChange={(v) => { const p = { ...preview, travel_mode: v }; setPreview(p); runPreview(p); }}>
                <SelectTrigger data-testid="preview-mode"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {["bike","car","suv","bus","train","flight"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <label className="text-[11px] uppercase text-slate-500 tracking-wider">Route distance (km)</label>
              <NumInput testid="preview-route" value={preview.route_km} onChange={v => { const p = { ...preview, route_km: v === "" ? 0 : v }; setPreview(p); runPreview(p); }}/>
              <label className="text-[11px] uppercase text-slate-500 tracking-wider">Detour distance (km)</label>
              <NumInput testid="preview-detour" value={preview.detour_km} onChange={v => { const p = { ...preview, detour_km: v === "" ? 0 : v }; setPreview(p); runPreview(p); }}/>
              <label className="text-[11px] uppercase text-slate-500 tracking-wider">Parcel size</label>
              <Select value={preview.size} onValueChange={(v) => { const p = { ...preview, size: v }; setPreview(p); runPreview(p); }}>
                <SelectTrigger data-testid="preview-size"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {["envelope","small","medium","large","xl"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {previewResult && (
              <div className="mt-4 rounded-lg bg-slate-50 p-3 text-sm space-y-1">
                <Row label="Handover" value={`₹${previewResult.handover_fee}`}/>
                <Row label={<span data-testid="preview-route-row">Route ({previewResult.route_km} km{previewResult.mode_kind === "transit" ? " · tier fee" : ` × ₹${previewResult.route_rate}`})</span>} value={`₹${previewResult.route_component}`}/>
                <Row label={<span data-testid="preview-detour-row">Detour ({previewResult.detour_km} km × ₹{previewResult.detour_rate})</span>} value={`₹${previewResult.detour_component}`}/>
                <Row label="Size" value={`₹${previewResult.size_component}`}/>
                {previewResult.min_floor_applied && <Row label={<span className="text-emerald-700" data-testid="preview-floor-row">Raised to minimum floor ({previewResult.floor_basis_km} km tier)</span>} value={`₹${previewResult.min_floor}`}/>}
                <hr className="border-slate-200 my-1"/>
                <Row label="Carrier payout" value={<b>₹{previewResult.carrier_payout}</b>}/>
                <Row label="Commission" value={`₹${previewResult.commission}`}/>
                <Row label="Sender total" value={<b className="text-lg">₹{previewResult.sender_total}</b>}/>
              </div>
            )}
            <p className="text-[10px] text-slate-400 mt-2">Uses saved (server-side) config — save your edits first to see them here.</p>
          </Card>

          <Card title={<><History className="w-4 h-4 inline mr-1"/> Change history</>}>
            {history.length === 0 && <div className="text-xs text-slate-400 italic">No changes yet.</div>}
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {history.map((h, i) => (
                <div key={i} className="text-xs border-b border-slate-100 pb-2 last:border-0">
                  <div className="text-slate-500">{new Date(h.created_at).toLocaleString()}</div>
                  <div className="text-[11px] text-slate-400 truncate">by {h.admin_email}</div>
                  <div className="mt-1 space-y-0.5">
                    {Object.keys(h.after || {}).map(k => (
                      <div key={k} className="text-[11px]">
                        <b>{k}</b>: <span className="line-through text-slate-400">{JSON.stringify(h.before?.[k])}</span>
                        <span className="text-emerald-700"> → {JSON.stringify(h.after?.[k])}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirm pricing changes</DialogTitle>
            <DialogDescription>The following values will change immediately for NEW parcels. Existing parcels keep their locked-in price.</DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-slate-50 p-3 max-h-[300px] overflow-y-auto text-xs" data-testid="save-confirm-list">
            {changes.map((c, i) => (
              <div key={i} className="py-1 border-b border-slate-200 last:border-0">
                <b>{c.field}</b>: <span className="line-through text-slate-400">{JSON.stringify(c.before)}</span>
                <span className="text-emerald-700"> → {JSON.stringify(c.after)}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-confirm-save" className="bg-slate-900 hover:bg-slate-800">Save {changes.length} change{changes.length > 1 ? "s" : ""}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Card = ({ title, children }) => (
  <div className="rounded-2xl bg-white border border-slate-200 p-4">
    <div className="text-xs uppercase tracking-wider text-slate-500 mb-3 font-medium">{title}</div>
    {children}
  </div>
);
const Grid = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>;
const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] uppercase text-slate-500 tracking-wider">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);
const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-2 py-0.5">
    <span className="text-slate-500 text-xs">{label}</span>
    <span className="text-slate-900 text-right tabular-nums">{value}</span>
  </div>
);

export default PricingConfig;
